/* ============================================================
 * upsell-stack.js
 * Drives the Upsell Stack section.
 *  - Live bundle total with MSRP + sale savings breakdown
 *  - Variant pickers update price + compare_at_price + variant id per row
 *  - One-click "Add Bundle" using Shopify /cart/add.js (multiple items)
 *  - Dispatches the theme's `cart:update` event so the cart drawer,
 *    cart icon, and cart items all update natively
 *  - Respects settings.cart_type: opens the cart drawer or redirects
 *    to the cart page
 * ============================================================ */
(function () {
  'use strict';

  const $$ = (root, sel) => Array.from(root.querySelectorAll(sel));
  const $ = (root, sel) => root.querySelector(sel);

  function formatMoney(cents, currencySymbol) {
    const n = (cents / 100).toFixed(2);
    return `${currencySymbol || '$'}${n}`;
  }

  // Updates an item's stored price/compare data + its visible price row.
  // Shared by the variant-select handler and the main-variant-picker sync.
  function applyPriceDisplay(item, price, compare, currency) {
    item.dataset.price = String(price);
    item.dataset.compare = String(compare);

    const priceEl = $(item, '[data-upsell-price]');
    const compareEl = $(item, '[data-upsell-compare]');
    const pctEl = $(item, '[data-upsell-pct]');
    if (priceEl) priceEl.textContent = formatMoney(price, currency);
    if (compare > price) {
      priceEl && priceEl.classList.add('is-sale');
      if (compareEl) {
        compareEl.textContent = formatMoney(compare, currency);
        compareEl.removeAttribute('hidden');
      }
      if (pctEl) {
        const p = Math.round(((compare - price) / compare) * 100);
        pctEl.textContent = `−${p}%`;
        pctEl.removeAttribute('hidden');
      }
    } else {
      priceEl && priceEl.classList.remove('is-sale');
      compareEl && compareEl.setAttribute('hidden', '');
      pctEl && pctEl.setAttribute('hidden', '');
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[c]));
  }

  function withWidth(src, width) {
    if (!src) return '';
    const sep = src.indexOf('?') === -1 ? '?' : '&';
    return `${src}${sep}width=${width}`;
  }

  // A variant's own image if it has one, else the product's default image.
  function variantImageSrc(variant, product) {
    return (
      (variant.featured_image && variant.featured_image.src) ||
      product.featured_image ||
      (product.images && product.images[0]) ||
      ''
    );
  }

  function buildVariantOptionsHtml(variants, selectedId, product) {
    return variants
      .map((v) => {
        const compare = v.compare_at_price || v.price;
        const disabled = !v.available;
        const label = escapeHtml(v.title) + (disabled ? ' — Sold out' : '');
        const imgSrc = variantImageSrc(v, product);
        const imageAttrs = imgSrc
          ? ` data-image="${escapeHtml(withWidth(imgSrc, 200))}" data-image-2x="${escapeHtml(withWidth(imgSrc, 400))}"`
          : '';
        return `<option value="${v.id}" data-price="${v.price}" data-compare="${compare}"${imageAttrs}${
          v.id === selectedId ? ' selected' : ''
        }${disabled ? ' disabled' : ''}>${label}</option>`;
      })
      .join('');
  }

  // Builds the same markup/data-attributes as snippets/upsell-card.liquid so
  // recalc(), the variant-change handler, and the checkbox toggle all work
  // unmodified against recommendation cards rendered this way.
  function buildRecommendationCardHtml(product, currency) {
    const variants = product.variants || [];
    const firstAvailable = variants.find((v) => v.available) || variants[0];
    if (!firstAvailable) return '';

    const price = firstAvailable.price;
    const compare = firstAvailable.compare_at_price || firstAvailable.price;
    const hasCompare = compare > price;
    const pct = hasCompare ? Math.round(((compare - price) / compare) * 100) : 0;
    const img = variantImageSrc(firstAvailable, product);
    const mediaHtml = img
      ? `<img src="${escapeHtml(withWidth(img, 200))}" srcset="${escapeHtml(withWidth(img, 200))} 1x, ${escapeHtml(
          withWidth(img, 400)
        )} 2x" width="200" height="200" loading="lazy" class="upsell-card__img" data-upsell-img alt="${escapeHtml(product.title)}">`
      : `<div class="upsell-card__img upsell-card__img--placeholder" data-upsell-img aria-hidden="true"></div>`;
    const metaHtml =
      variants.length <= 1
        ? escapeHtml((firstAvailable.title || '').replace('Default Title', ''))
        : `<select class="upsell-card__variant" data-upsell-variant-select aria-label="Choose variant for ${escapeHtml(
            product.title
          )}">${buildVariantOptionsHtml(variants, firstAvailable.id, product)}</select>`;

    return `<div class="upsell-card is-checked" role="listitem" data-upsell-item data-product-id="${product.id}" data-variant-id="${firstAvailable.id}" data-price="${price}" data-compare="${compare}" data-available="${!!firstAvailable.available}" data-locked="false">
  <label class="upsell-card__label">
    <div class="upsell-card__media">${mediaHtml}</div>
    <div class="upsell-card__info">
      <div class="upsell-card__title-row"><span class="upsell-card__title">${escapeHtml(product.title)}</span></div>
      <div class="upsell-card__meta">${metaHtml}</div>
      <div class="upsell-card__price-row" data-upsell-price-row>
        <span class="upsell-card__price${hasCompare ? ' is-sale' : ''}" data-upsell-price>${formatMoney(price, currency)}</span>
        <s class="upsell-card__compare" data-upsell-compare${hasCompare ? '' : ' hidden'}>${
      hasCompare ? formatMoney(compare, currency) : ''
    }</s>
        <span class="upsell-card__pct" data-upsell-pct${hasCompare ? '' : ' hidden'}>${
      hasCompare ? `−${pct}%` : ''
    }</span>
      </div>
    </div>
    <input type="checkbox" class="upsell-card__check" data-upsell-check checked aria-label="Add ${escapeHtml(product.title)} to bundle" />
  </label>
</div>`;
  }

  function initRecommendations(root, currency) {
    const el = $(root, '[data-upsell-reco]');
    if (!el || !el.dataset.url) return;

    const limit = parseInt(el.dataset.limit || '4', 10);
    const currentProductId = String(el.dataset.currentProductId || '');
    const excludedIds = (el.dataset.excludedIds || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    // Fetch immediately rather than waiting for scroll-into-view: this
    // widget usually sits just below the buy box, so an eager fetch of
    // Shopify's lean recommendations JSON (a few KB, vs. re-rendering the
    // whole parent section) is normally resolved before the customer
    // scrolls to it at all.
    fetch(el.dataset.url)
      .then((res) => {
        if (!res.ok) throw new Error(`Recommendations fetch failed (${res.status})`);
        return res.json();
      })
      .then((data) => {
        const products = Array.isArray(data.products) ? data.products : [];
        const cardsHtml = [];
        for (const product of products) {
          if (cardsHtml.length >= limit) break;
          if (String(product.id) === currentProductId) continue;
          if (!product.available) continue;
          if (excludedIds.includes(String(product.id))) continue;
          const html = buildRecommendationCardHtml(product, currency);
          if (html) cardsHtml.push(html);
        }
        if (cardsHtml.length) {
          el.outerHTML = cardsHtml.join('');
        } else {
          el.remove();
        }
      })
      .catch((err) => {
        console.error('[upsell-stack]', err);
        el.remove();
      });
  }

  // Keeps the locked "this item" card in sync with the PDP's own variant
  // picker. That component (assets/variant-picker.js) dispatches the
  // theme's `variant:update` event with the freshly re-rendered section
  // HTML already in hand — we just lift the same widget's locked card back
  // out of that document rather than re-deriving price/image ourselves, so
  // it always matches exactly what a fresh page load for that variant would
  // produce.
  function initMainVariantSync(root) {
    const lockedItem = $(root, '[data-upsell-item][data-locked="true"]');
    if (!lockedItem) return;
    const productId = lockedItem.dataset.productId;
    const blockId = root.dataset.blockId;

    // Listen on document rather than the closest .shopify-section: when
    // this widget is used as its own standalone section (sections/upsell-
    // stack.liquid) rather than nested inside the main product section, its
    // own .shopify-section wrapper is a different subtree than the one
    // containing the variant picker, so a scoped listener would never see
    // this bubbling event at all. The productId check below provides the
    // actual scoping instead.
    document.addEventListener('variant:update', (event) => {
      const detail = event.detail || {};
      const eventProductId = detail.data && detail.data.productId;
      if (!eventProductId || String(eventProductId) !== String(productId)) return;

      const doc = detail.data && detail.data.html;
      if (!doc) return;

      const freshStack = blockId ? doc.querySelector(`[data-block-id="${blockId}"]`) : doc.querySelector('[data-upsell-stack]');
      const freshLockedItem = freshStack && $(freshStack, '[data-upsell-item][data-locked="true"]');
      const currentLockedItem = $(root, '[data-upsell-item][data-locked="true"]');
      if (!freshLockedItem || !currentLockedItem) return;

      currentLockedItem.replaceWith(freshLockedItem);
    });
  }

  function initStack(root) {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const config = {
      currency: root.dataset.currency || '$',
    };

    let items = $$(root, '[data-upsell-item]');
    function refreshItems() {
      items = $$(root, '[data-upsell-item]');
    }

    const totalEl = $(root, '[data-upsell-total]');
    const msrpRow = $(root, '[data-upsell-msrp-row]');
    const compareSubtotalEl = $(root, '[data-upsell-compare-subtotal]');
    const savingsRow = $(root, '[data-upsell-savings-row]');
    const savingsEl = $(root, '[data-upsell-savings]');
    const saveBadgeEl = $(root, '[data-upsell-save-badge]');
    const savePctEl = $(root, '[data-upsell-save-pct]');
    const countEl = $(root, '[data-upsell-count]');
    const ctaLabel = $(root, '[data-upsell-cta-label]');
    const addBtn = $(root, '[data-upsell-add]');
    const statusEl = $(root, '[data-upsell-status]');

    function getSelected() {
      return items.filter((it) => {
        const cb = $(it, '[data-upsell-check]');
        return cb && cb.checked;
      });
    }

    function recalc() {
      const selected = getSelected();
      const count = selected.length;
      const subtotalCents = selected.reduce(
        (s, el) => s + parseInt(el.dataset.price || '0', 10),
        0
      );
      const compareCents = selected.reduce(
        (s, el) => s + parseInt(el.dataset.compare || el.dataset.price || '0', 10),
        0
      );

      const totalSavings = Math.max(0, compareCents - subtotalCents);
      const savePct = compareCents > 0 ? Math.round((totalSavings / compareCents) * 100) : 0;

      // Update visual states
      items.forEach((it) => {
        const cb = $(it, '[data-upsell-check]');
        it.classList.toggle('is-checked', !!(cb && cb.checked));
      });

      if (countEl) {
        countEl.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
      }
      if (compareSubtotalEl) {
        compareSubtotalEl.textContent = formatMoney(compareCents, config.currency);
      }
      if (totalEl) totalEl.textContent = formatMoney(subtotalCents, config.currency);

      // MSRP row + savings row visible only when there's a sale
      if (totalSavings > 0) {
        msrpRow && msrpRow.removeAttribute('hidden');
        savingsRow && savingsRow.removeAttribute('hidden');
        if (savingsEl) savingsEl.textContent = formatMoney(totalSavings, config.currency);
        saveBadgeEl && saveBadgeEl.removeAttribute('hidden');
        if (savePctEl) savePctEl.textContent = String(savePct);
      } else {
        msrpRow && msrpRow.setAttribute('hidden', '');
        savingsRow && savingsRow.setAttribute('hidden', '');
        saveBadgeEl && saveBadgeEl.setAttribute('hidden', '');
      }

      // A locked item can end up pointed at a sold-out variant through
      // forces outside this widget (the main product's own variant picker,
      // or a `?variant=` deep link) — its checkbox can't be unticked to
      // work around that, so block the whole bundle rather than let
      // /cart/add.js reject the request for everyone in it.
      const unavailableSelected = selected.find((el) => el.dataset.available === 'false');

      if (unavailableSelected) {
        if (ctaLabel) ctaLabel.textContent = 'Sold Out';
        if (addBtn) addBtn.disabled = true;
        if (statusEl) {
          statusEl.textContent = unavailableSelected.dataset.locked === 'true'
            ? 'This item is sold out in the selected option. Choose a different variant to continue.'
            : 'One of the selected items is sold out in the selected option.';
          statusEl.dataset.upsellSoldOut = 'true';
        }
        return;
      }
      // Only clear the status message if we're the ones who set it — leave
      // an unrelated add-to-cart success/failure message alone.
      if (statusEl && statusEl.dataset.upsellSoldOut === 'true') {
        statusEl.textContent = '';
        delete statusEl.dataset.upsellSoldOut;
      }

      if (ctaLabel) {
        ctaLabel.textContent = count === 0
          ? 'Pick an Item'
          : count === 1
            ? 'Add to Cart'
            : `Add Bundle (${count})`;
      }
      if (addBtn) addBtn.disabled = count === 0;
    }

    // Event delegation: checkbox toggles + variant changes
    root.addEventListener('change', (e) => {
      const target = e.target;
      if (target.matches('[data-upsell-check]')) {
        if (target.hasAttribute('data-locked')) {
          target.checked = true;
          return;
        }
        recalc();
      } else if (target.matches('[data-upsell-variant-select]')) {
        const item = target.closest('[data-upsell-item]');
        const opt = target.options[target.selectedIndex];
        const newPrice = parseInt(opt.dataset.price || '0', 10);
        const newCompare = parseInt(opt.dataset.compare || opt.dataset.price || '0', 10);
        item.dataset.variantId = opt.value;
        item.dataset.available = String(!opt.disabled);
        applyPriceDisplay(item, newPrice, newCompare, config.currency);

        // Swap the thumbnail to match the selected variant, if it has one.
        const imgEl = $(item, '[data-upsell-img]');
        if (imgEl && opt.dataset.image) {
          if (imgEl.tagName === 'IMG') {
            imgEl.src = opt.dataset.image;
            if (opt.dataset.image2x) {
              imgEl.srcset = `${opt.dataset.image} 1x, ${opt.dataset.image2x} 2x`;
            } else {
              imgEl.removeAttribute('srcset');
            }
          } else {
            // Was rendered as the no-image placeholder; upgrade it to a real <img>.
            const newImg = document.createElement('img');
            newImg.className = 'upsell-card__img';
            newImg.loading = 'lazy';
            newImg.width = 200;
            newImg.height = 200;
            newImg.setAttribute('data-upsell-img', '');
            newImg.alt = item.querySelector('.upsell-card__title')?.textContent || '';
            newImg.src = opt.dataset.image;
            if (opt.dataset.image2x) newImg.srcset = `${opt.dataset.image} 1x, ${opt.dataset.image2x} 2x`;
            imgEl.replaceWith(newImg);
          }
        }

        recalc();
      }
    });

    if (addBtn) {
      addBtn.addEventListener('click', async () => {
        const selected = getSelected();
        if (!selected.length) return;

        addBtn.disabled = true;
        if (ctaLabel) ctaLabel.textContent = 'Adding…';

        const sectionIds = [];
        document.querySelectorAll('cart-items-component').forEach((el) => {
          if (el.dataset && el.dataset.sectionId) {
            sectionIds.push(el.dataset.sectionId);
          }
        });

        const payload = {
          items: selected.map((el) => ({
            id: parseInt(el.dataset.variantId, 10),
            quantity: 1,
          })),
        };
        if (sectionIds.length) {
          payload.sections = sectionIds.join(',');
        }

        const cartAddUrl =
          (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url)
            ? Theme.routes.cart_add_url
            : '/cart/add.js';

        const cartType = root.dataset.cartType || 'drawer';

        try {
          const res = await fetch(cartAddUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (!res.ok) throw new Error(`Cart add failed (${res.status})`);
          const responseData = await res.json();

          if (statusEl) statusEl.textContent = `Added ${selected.length} item${selected.length === 1 ? '' : 's'} to cart.`;

          const cartEvent = new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: {},
              sourceId: 'upsell-stack',
              data: {
                source: 'product-form-component',
                itemCount: selected.length,
                sections: responseData.sections || {},
              },
            },
          });
          document.dispatchEvent(cartEvent);

          if (cartType === 'page') {
            const cartUrl =
              (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url)
                ? Theme.routes.cart_url
                : '/cart';
            window.location.href = cartUrl;
            return;
          }

          if (ctaLabel) ctaLabel.textContent = 'Added ✓';
          // Defer to recalc() rather than blindly restoring originalLabel/
          // disabled=false — if availability changed while this add was in
          // flight (e.g. the main variant picker fired mid-request), recalc()
          // will correctly re-apply the sold-out/disabled state instead of
          // stomping it back to an enabled button with a stale label.
          setTimeout(recalc, 1400);
        } catch (err) {
          console.error('[upsell-stack]', err);
          if (statusEl) statusEl.textContent = 'Could not add to cart. Try again.';
          recalc();
        }
      });
    }

    // Recommended products (when product_source is 'recommendations') load
    // asynchronously via initRecommendations(). Watch the list for that DOM
    // swap so newly added cards are picked up.
    const listEl = $(root, '.upsell-stack__list');
    if (listEl && typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(() => {
        refreshItems();
        recalc();
      });
      observer.observe(listEl, { childList: true, subtree: true });
    }

    initRecommendations(root, config.currency);
    initMainVariantSync(root);

    recalc();
  }

  function init() {
    document.querySelectorAll('[data-upsell-stack]').forEach(initStack);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify theme editor section load
  document.addEventListener('shopify:section:load', init);
})();
